import React from 'react';
import { CheckboxCheckedIcon, EditIcon3, DeleteIcon } from 'assets';
import { JobCriteriaData } from '../types';
import { CoreResponsibilitiesEditModal, DeleteSectionModal } from 'components';
import { CoreResponsibilitiesPayload, useCoreResponsibilitiesEdit, useCoreResponsibilitiesDelete, useToast } from 'hooks';
import isEqual from 'lodash/isEqual';

interface CoreResponsibilitiesSectionProps {
  assessmentId: string | null;
  data: JobCriteriaData['coreResponsibilities'];
  onEdit: () => void;
  onDelete: () => void;
  onSave: (updatedData: JobCriteriaData['coreResponsibilities']) => void;
  disabled?: boolean;
}

const initialData = {
  responsibilities: []
};

const CoreResponsibilitiesSection: React.FC<CoreResponsibilitiesSectionProps> = ({
  assessmentId,
  data,
  onEdit,
  onDelete,
  onSave,
  disabled
}) => {
  const { openToast } = useToast();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

  const {
    isSubmitting,
    submitCoreResponsibilities
  } = useCoreResponsibilitiesEdit(assessmentId);

  const {
    deleteCoreResponsibilities,
  } = useCoreResponsibilitiesDelete(assessmentId);

  const onSubmit = (formData: JobCriteriaData['coreResponsibilities']) => {
    if (!assessmentId || isSubmitting) {
      openToast({
        show: true,
        heading: "Required",
        text: "Assessment ID is required",
        type: "error",
        timeOut: 5000,
      });
      return;
    }

    const payload: CoreResponsibilitiesPayload = {
      assessment_id: assessmentId,
      ...formData.responsibilities.reduce((acc, resp, index) => ({
        ...acc,
        [`responsibility${index + 1}`]: resp.title,
        [`check${index + 1}`]: resp.checked ?? false,
      }), {})
    };

    submitCoreResponsibilities(payload, {
      onSuccess: () => {
        onSave(formData);
        setIsModalOpen(false);
      },
      onError: (_err) => {
        setIsModalOpen(false);
      },
    });
  };

  const handleDeleteConfirm = () => {
    if (!assessmentId) {
      openToast({
        show: true,
        heading: "Required",
        text: "Assessment ID is required for deletion",
        type: "info",
        timeOut: 5000,
      });
      return;
    }

    deleteCoreResponsibilities({ assessment_id: assessmentId }, {
      onSuccess: () => {
        onDelete();
        setIsDeleteModalOpen(false);
      },
    });
  };

  if (isEqual(data, initialData)) return null;
  if (!data) return null;

  return (
    <section className="border-t border-[#dfe1e6] pt-6 mb-8 relative">
      <div className="flex justify-between items-center mb-2.5">
        <h2 className="text-xl sm:text-2xl font-medium sm:font-semibold m-0">Core Responsibilities</h2>
        {!disabled && (
          <div className="flex gap-3.5 items-center">
            <span className="cursor-pointer">
              <EditIcon3 onClick={() => { onEdit(); setIsModalOpen(true); }} />
            </span>
            <div className="relative group cursor-pointer">
              <DeleteIcon onClick={() => setIsDeleteModalOpen(true)} />
              <div className="absolute bottom-full right-0 mb-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-52 z-10">
                <span className="block bg-[#1d2939] text-white text-xs rounded-lg px-3.5 py-2.5 leading-relaxed">
                  You can delete section you don't want to screen at this stage
                </span>
                <span className="absolute top-full right-[18px] w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-[#1d2939]" />
              </div>
            </div>
          </div>
        )}
      </div>

      <p className="text-[#505f79] mb-3.5 text-[15px]">These areas define the core responsibilities and day-to-day expectations.</p>

      <div className="grid sm:grid-cols-2 gap-x-8">
        {data.responsibilities.map((responsibility, index) => (
          responsibility.checked && (
            <div key={index} className="flex items-start gap-3">
              <div className="mt-1">
                <div className="w-7 h-7 rounded flex items-center justify-center">
                  <CheckboxCheckedIcon />
                </div>
              </div>
              <div>
                <h3 className="text-base font-bold m-0 mb-2">{responsibility.title}</h3>
                <p className="text-sm text-[#505f79] m-0">{responsibility.description}</p>
              </div>
            </div>
          )
        ))}
      </div>

      <CoreResponsibilitiesEditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        currentData={data}
        onSave={onSubmit}
        isSubmitting={isSubmitting}
      />

      <DeleteSectionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        sectionName="Core Responsibilities"
      />
    </section>
  );
};

export default CoreResponsibilitiesSection;
